// ═══════════════════════════════════════════════════════════════════════════
// Dynamic Blocks — Lookup Table + Block Properties Table (DYN-2)
// ═══════════════════════════════════════════════════════════════════════════
//
// AutoCAD Lookup Table (BLOOKUPTABLE dialog): a spreadsheet-like structure
// that maps rows of input parameter values to a single output value. Used
// by the Lookup Parameter / Lookup Action pair to implement "when this
// dropdown option is chosen, set these parameters to these values".
//
// AutoCAD Block Properties Table (BTABLE dialog): a user-defined set of
// custom properties attached to the block (name, type, default value,
// optional formula). Shown in the Properties Palette alongside the
// standard INSERT attributes. Formula evaluation is DYN-4.
//
// We ship the schemas now (DYN-2) so DXF round-trip preserves the data;
// the editor dialogs land in DYN-2.4 / DYN-2.5.

import { z } from "zod";

// ═══════════════════════════════════════════════════════════════════════════
// Lookup Table
// ═══════════════════════════════════════════════════════════════════════════

/**
 * A lookup table input column. Points to ONE parameter and ONE of its
 * numeric / boolean / string-valued properties to match against the row
 * input value.
 *
 * `propertyKey` enumerates the per-kind field the row cell is compared
 * against. AutoCAD uses the parameter's DXF property key (e.g. "Distance1"
 * for Linear). We normalise to our schema field names.
 *
 * Allowed keys (subset — extended as more parameter kinds support lookup):
 *   - "currentDistance" → Linear, Polar
 *   - "currentAngle"    → Polar, Rotation
 *   - "currentX"        → Point, XY
 *   - "currentY"        → Point, XY
 *   - "currentValue"    → Flip (boolean)
 *   - "currentStateId"  → Visibility (string)
 */
export const lookupTableColumnSchema = z.object({
  id: z.string().min(1),
  parameterId: z.string().min(1),
  propertyKey: z.enum([
    "currentDistance",
    "currentAngle",
    "currentX",
    "currentY",
    "currentValue",
    "currentStateId",
  ]),
});
export type LookupTableColumn = z.infer<typeof lookupTableColumnSchema>;

/**
 * The single output column. Rows optionally carry an `outputValue` (union
 * of number / string / boolean) that the evaluator / reverse-lookup
 * dropdown exposes. Output is informational for Tier 2 — the cascade only
 * writes back to input parameters.
 */
export const lookupTableOutputColumnSchema = z.object({
  id: z.string().min(1),
  label: z.string(),
});
export type LookupTableOutputColumn = z.infer<
  typeof lookupTableOutputColumnSchema
>;

/**
 * A lookup table row. `inputValues` is a map from input column id to the
 * value that must match at that position for the row to be selected. Row
 * ids are stable (they appear in LookupParameter.currentLookupRowId).
 */
export const lookupTableRowSchema = z.object({
  id: z.string().min(1),
  inputValues: z.record(
    z.string(),
    z.union([z.number(), z.string(), z.boolean()]),
  ),
  outputValue: z.union([z.number(), z.string(), z.boolean()]).optional(),
});
export type LookupTableRow = z.infer<typeof lookupTableRowSchema>;

/**
 * A complete lookup table — a dictionary entry on the block definition.
 * Referenced from a LookupParameter by `lookupTableId`.
 */
export const lookupTableSchema = z.object({
  id: z.string().min(1),
  name: z.string().default(""),
  inputColumns: z.array(lookupTableColumnSchema).default([]),
  outputColumn: lookupTableOutputColumnSchema.optional(),
  rows: z.array(lookupTableRowSchema).default([]),
});
export type LookupTable = z.infer<typeof lookupTableSchema>;

// ═══════════════════════════════════════════════════════════════════════════
// Block Properties Table (BTABLE)
// ═══════════════════════════════════════════════════════════════════════════

/**
 * A single entry in the block properties table. Formulas are DYN-4 — the
 * parser stores the raw expression string here without evaluating.
 */
export const blockPropertyEntrySchema = z.object({
  key: z.string().min(1),
  label: z.string().default(""),
  type: z.enum(["string", "number", "boolean"]).default("string"),
  defaultValue: z.union([z.number(), z.string(), z.boolean()]).optional(),
  // DYN-4: formula expressions. We round-trip the string unchanged; a
  // future parser wraps it with validation and sandboxed evaluation.
  formula: z.string().optional(),
});
export type BlockPropertyEntry = z.infer<typeof blockPropertyEntrySchema>;

// ─── DYN-4 Fix 5 — constraint-based Block Properties Table ─────────────────
//
// AutoCAD stores BOTH the legacy user-defined BTABLE (`entries[]` above) AND
// the constraint-based Block Properties Table in the same DXF object class
// `ACDBBLOCKPROPERTIESTABLE`. The constraint-based form encodes a
// spreadsheet where:
//
//   - a single UserVariable column (the "key") is driven by a
//     BLOCKUSERPARAMETER (not a BLOCKLOOKUPPARAMETER);
//   - each additional column reflects the value of a CONSTRAINT (distance,
//     angle) or another user variable (real, string, area);
//   - each row pins every column to a concrete value for that key choice.
//
// In АБИ1.dxf (ГОСТ 8239-89 ibeam) the table has 17 rows (profile numbers
// 10…60) × 9 columns (profile, h, b, s, t, R1, r2, Площадь, Масса). When
// the user picks a profile from the Properties palette dropdown, AutoCAD
// replays the matching row into every linked BLOCKUSERPARAMETER /
// CONSTRAINT output.
//
// We ship the schema now so the parser (Fix 5 below) can preserve the
// whole payload; the evaluator wiring (DYN-4 Phase 3 / Fix 6) is where
// rows actually feed the parameter graph.
//
// Kinds correspond to the DXF group-92 column kind code, widened to cover
// the five AutoCAD emits (UserVariable / UpdatedDistance / UpdatedAngle /
// UpdatedReal / UpdatedString). `unknown` is the catch-all for forward
// compatibility.
export const blockPropertiesTableColumnSchema = z.object({
  index: z.number(),
  kind: z.enum([
    "user-variable",
    "updated-distance",
    "updated-angle",
    "updated-real",
    "updated-string",
    "unknown",
  ]),
  // DXF group 301 — the column class name as AutoCAD writes it
  // ("UserVariable", "UpdatedDistance", "UpdatedAngle", "UpdatedReal",
  // "UpdatedString", …). Preserved verbatim so the writer can re-emit
  // the same literal.
  columnName: z.string().default(""),
  // DXF group 300 — display name shown in the Properties palette. For
  // UserVariable this is the driver parameter label ("Двутавр"); for
  // UpdatedDistance it is the linked CONSTRAINT name ("h", "b", …).
  displayName: z.string().default(""),
  // DXF group 340 — handle of the BLOCKUSERPARAMETER (for UserVariable
  // column) or the CONSTRAINT object whose value is driven by the row
  // (for Updated* columns). Empty string when no link exists.
  linkHandle: z.string().default(""),
  // DXF group 170 — declared value type for the column cells. We widen
  // AutoCAD's ints (0=real, 1=distance, 2=area, 3=angle, 5=string) to
  // named literals for readable assertions.
  valueType: z
    .enum(["real", "distance", "area", "angle", "string", "unknown"])
    .default("unknown"),
});
export type BlockPropertiesTableColumn = z.infer<
  typeof blockPropertiesTableColumnSchema
>;

export const blockPropertiesTableRowSchema = z.object({
  index: z.number(),
  // DXF group 170 — per-row state flag. AutoCAD emits 1 for every
  // authored row; the default of 1 matches.
  flag: z.number().default(1),
  // DXF group 300 — row display key (e.g. "10", "20", "60"). Matches the
  // key-column's cell value for that row.
  key: z.string().default(""),
  // Per-column cell values in declaration order. Strings come from
  // group 300 inside the row block; numbers from group 140. Length
  // should equal the containing table's `columns.length`; the parser
  // pads with 0 when AutoCAD wrote fewer cells than declared columns.
  values: z.array(z.union([z.number(), z.string()])).default([]),
});
export type BlockPropertiesTableRow = z.infer<
  typeof blockPropertiesTableRowSchema
>;

export const blockPropertiesTableKeyColumnSchema = z.object({
  // Which column is the dropdown key. AutoCAD puts it at index 0 in
  // every sample we have; keep as data rather than a convention so the
  // parser round-trips unusual authoring exactly.
  columnIndex: z.number().default(0),
  // Handle of the BLOCKUSERPARAMETER that drives this table's key.
  driverParameterHandle: z.string().default(""),
  // Display name of that driver parameter (mirrors columns[keyIndex].
  // displayName for convenience).
  driverParameterName: z.string().default(""),
});
export type BlockPropertiesTableKeyColumn = z.infer<
  typeof blockPropertiesTableKeyColumnSchema
>;

export const blockPropertiesTableSchema = z.object({
  // Legacy DYN-2 BTABLE — user-defined per-block custom properties.
  // Preserved as-is so existing consumers (BlockPropertiesTableDialog,
  // command-engine dynamicLookupMutations, writer/round-trip tests)
  // keep working without churn. New constraint-based tables leave this
  // empty and populate `columns` / `rows` / `keyColumn` instead.
  entries: z.array(blockPropertyEntrySchema).default([]),
  // ── DYN-4 Fix 5 — constraint-based key-column table ──
  // All fields are optional: legacy BTABLE emitters (and every existing
  // in-memory construction of `BlockPropertiesTable` in
  // `dynamicLookupMutations`, `BlockPropertiesTableDialog`, tests) only
  // populate `entries`. The DXF parser populates the fields below when
  // the constraint-based form is detected (AutoCAD 2018+ BTABLE with
  // `90 columnCount`). Keeping them optional lets both shapes coexist
  // on the single discriminator-free type without a breaking migration.
  id: z.string().optional(),
  handle: z.string().optional(),
  name: z.string().optional(),
  columns: z.array(blockPropertiesTableColumnSchema).optional(),
  rows: z.array(blockPropertiesTableRowSchema).optional(),
  keyColumn: blockPropertiesTableKeyColumnSchema.optional(),
});
export type BlockPropertiesTable = z.infer<typeof blockPropertiesTableSchema>;

// ═══════════════════════════════════════════════════════════════════════════
// Lookup helpers
// ═══════════════════════════════════════════════════════════════════════════

export function findLookupTableById(
  tables: readonly LookupTable[],
  id: string,
): LookupTable | undefined {
  return tables.find((t) => t.id === id);
}

export function findLookupRowById(
  table: LookupTable,
  id: string,
): LookupTableRow | undefined {
  return table.rows.find((r) => r.id === id);
}

/**
 * Match a row by comparing each input column's current value (looked up
 * from the runtime parameter snapshot) against the row's inputValues. The
 * first exact match wins (AutoCAD semantics — first row in table order
 * with matching inputs is selected).
 *
 * `runtimeInputs` is a map keyed by lookup table column id with the value
 * currently held by the parameter referenced by that column. The matcher
 * compares with === (ties preserved; numeric tolerance is not applied —
 * AutoCAD uses exact-value matching on authored rows).
 *
 * Returns undefined if no row matches.
 */
export function findLookupRowByInputs(
  table: LookupTable,
  runtimeInputs: Readonly<Record<string, number | string | boolean>>,
): LookupTableRow | undefined {
  for (const row of table.rows) {
    let allMatch = true;
    for (const col of table.inputColumns) {
      const expected = row.inputValues[col.id];
      const actual = runtimeInputs[col.id];
      if (expected === undefined && actual === undefined) continue;
      if (expected !== actual) {
        allMatch = false;
        break;
      }
    }
    if (allMatch) return row;
  }
  return undefined;
}
